import { RadioGroup } from '@headlessui/react'
import { CheckCircleIcon } from '@heroicons/react/solid'
import classNames from 'classnames'
import { ComponentProps, ComponentType, ReactNode } from 'react'
import { Label } from '../Label'

type Value = string | null

export interface RadioCardOption<V extends Value = Value> {
  id: V
  title: string
  description?: string
  disabled?: boolean
  icon?: ComponentType<ComponentProps<'svg'>>
}

export interface RadioCardProps<V extends Value = Value> {
  className?: string
  cols?: number
  disabled?: boolean
  label?: string
  onChange?: (value: RadioCardOption<V>) => void
  options: RadioCardOption<V>[]
  value?: RadioCardOption<V>
}

export const RadioCards = <V extends Value = Value>({
  className,
  value,
  onChange,
  options = [],
  label,
  disabled,
  cols,
}: RadioCardProps<V>): ReactNode => {
  let colsClass: string

  switch (cols) {
    case 1:
      colsClass = 'sm:grid-cols-1'
      break

    case 2:
      colsClass = 'sm:grid-cols-2'
      break

    case 3:
      colsClass = 'sm:grid-cols-3'
      break

    case 4:
      colsClass = 'sm:grid-cols-4'
      break
    default:
      // If cols wasn't specified, we should calculate the best
      // fit based on the number of options.
      colsClass =
        options.length === 2
          ? 'sm:grid-cols-2'
          : options.length === 4
          ? 'sm:grid-cols-4'
          : 'sm:grid-cols-3'
  }

  return (
    <RadioGroup className={className} disabled={disabled} by="id" value={value} onChange={onChange}>
      {label && <Label className="mb-2">{label}</Label>}
      <div className={`grid grid-cols-1 gap-y-4 ${colsClass} sm:gap-x-4`}>
        {options.map((option, index) => (
          <RadioGroup.Option
            key={option.id ?? index}
            value={option}
            disabled={option.disabled}
            className={({ checked, active }) =>
              classNames(
                checked ? 'border-transparent' : 'border-border',
                {
                  'border-violet ring-2 ring-violet': active,
                  'opacity-70 cursor-not-allowed': disabled || option.disabled,
                  'cursor-pointer': !disabled && !option.disabled,
                },
                'relative flex rounded-lg border bg-background-input p-4 shadow-sm focus:outline-none'
              )
            }
          >
            {({ checked, active }) => (
              <>
                <span className="flex flex-1 items-center">
                  {option.icon && (
                    <option.icon
                      className={classNames(
                        'h-5 w-5 mr-3',
                        checked ? 'text-violet' : 'text-gray-400'
                      )}
                      aria-hidden="true"
                    />
                  )}
                  <span className="flex flex-col">
                    <RadioGroup.Label as="span" className="block text-sm font-medium">
                      {option.title}
                    </RadioGroup.Label>
                    {option.description && (
                      <RadioGroup.Description
                        as="span"
                        className="mt-1 flex items-center text-sm text-gray-500"
                      >
                        {option.description}
                      </RadioGroup.Description>
                    )}
                  </span>
                </span>
                <CheckCircleIcon
                  className={classNames(!checked ? 'invisible' : '', 'h-5 w-5 text-violet')}
                  aria-hidden="true"
                />
                <span
                  data-test-id={option.id}
                  className={classNames(
                    active ? 'border' : 'border-2',
                    checked ? 'border-violet' : 'border-transparent',
                    'pointer-events-none absolute -inset-px rounded-lg'
                  )}
                  aria-hidden="true"
                />
              </>
            )}
          </RadioGroup.Option>
        ))}
      </div>
    </RadioGroup>
  )
}
